import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { AuthShell } from "@/components/AuthShell";
import { FormError } from "@/components/FormError";
import { authApi } from "@/api/endpoints/auth";

const schema = z.object({ email: z.string().email("Enter a valid email.") });
type FormValues = z.infer<typeof schema>;

export function ForgotPasswordPage(): JSX.Element {
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();
  const mutation = useMutation({
    mutationFn: (v: FormValues) => authApi.forgotPassword(schema.parse(v)),
    onSuccess: () => setSubmitted(true),
    // Always show neutral success on error too — backend never reveals existence.
    onError: () => setSubmitted(true),
  });

  if (submitted) {
    return (
      <AuthShell sideTitle="Reset link sent.">
        <div>
          <h1>Check your email</h1>
          <p>If an account with that email exists we have sent a password-reset link.</p>
        </div>
        <Link to="/login" className="btn btn-outline btn-lg btn-block">
          Back to sign in
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell sideTitle="Forgot password?">
      <div>
        <h1>Reset your password</h1>
        <p>Enter the email you signed up with. We will email a reset link.</p>
      </div>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))} noValidate>
        <div className="field-group">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            className="field-input"
            data-testid="forgot-email"
            {...register("email", { required: "Email is required." })}
          />
          <FormError message={errors.email?.message} />
        </div>
        <button
          type="submit"
          className="btn btn-primary btn-lg btn-block"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? "Sending…" : "Send reset link"}
        </button>
        <div className="auth-foot">
          <Link to="/login">Back to sign in</Link>
        </div>
      </form>
    </AuthShell>
  );
}
